import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, MessageSquare, User, FileText, Package, History } from 'lucide-react';
import { GolfBagIcon } from '@golfauth/ui';
import { cn } from '../../lib/utils';
import { goToCore } from '../../lib/goToCore';
import { arrivedFromCore } from '../../lib/arrivedFromCore';
import { useProfileStrengthScore } from '../../hooks/useProfileStrengthScore';

const fittingModules = [
  { name: 'Dashboard', path: '/golfer/dashboard', icon: LayoutDashboard },
  { name: 'Configuration History', path: '/golfer/config-history', icon: History },
  { name: 'Current Quotes', path: '/golfer/current-quotes', icon: FileText },
  { name: 'Orders', path: '/golfer/orders', icon: Package },
  { name: 'Messages', path: '/golfer/messages', icon: MessageSquare },
  { name: 'Profile', path: '/golfer/profile', icon: User },
];

export default function GolferSidebar() {
  const location = useLocation();
  const profileStrengthScore = useProfileStrengthScore();
  const showProfileStrengthDot =
    profileStrengthScore !== undefined && profileStrengthScore < 85;

  return (
    <aside
      data-sidebar="true"
      className={
        'hidden md:block w-56 shrink-0 border-r shadow-sm ' +
        'border-[var(--color-border)] bg-[var(--color-surface)] ' +
        'dark:border-slate-700 dark:bg-slate-800/50 dark:shadow-none'
      }
    >
      <nav className="p-4 space-y-1">
        {/* My Bag — see GolferBottomNav for the rationale on hiding it
            when arrivedFromCore() — same SFSafariViewController-popover
            loop concern applies on iPad-width screens that render this
            sidebar. */}
        {!arrivedFromCore() && (
          <button
            type="button"
            onClick={goToCore}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
              'text-on-subtle hover:bg-[var(--color-surface-overlay)] hover:text-on-surface border border-transparent',
              'dark:text-slate-300 dark:hover:bg-slate-700'
            )}
          >
            <GolfBagIcon className="w-5 h-5 shrink-0 object-contain opacity-90" />
            My Bag
          </button>
        )}
        {/* Home was a redirect to Core (via GolferLandingPage) — redundant
            with My Bag above, and the intermediate loading page + query
            string was tripping stale SW installs. Removed 2026-07-02. */}
        <p className="px-3 py-2 text-xs font-bold text-on-surface uppercase tracking-wider">
          GolfAuth Fittings
        </p>
        {fittingModules.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const isProfile = item.path === '/golfer/profile';
          return (
            <Link
              key={item.path}
              to={item.path}
              data-nav-active={isActive ? 'true' : undefined}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'border border-transparent dark:bg-[var(--color-brand-subtle)] dark:text-[var(--color-brand)] dark:border-brand-500/40'
                  : 'text-on-subtle hover:bg-[var(--color-surface-overlay)] hover:text-on-surface border border-transparent dark:text-slate-300 dark:hover:bg-slate-700'
              )}
              aria-label={isProfile && showProfileStrengthDot ? 'Profile — complete your profile' : undefined}
            >
              <span className="relative inline-flex shrink-0">
                <Icon className="w-5 h-5 shrink-0" />
                {isProfile && showProfileStrengthDot && (
                  <span className="absolute top-0 right-0 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-[var(--color-surface)] dark:ring-slate-800/50" />
                )}
              </span>
              {item.name}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
